import React from "react";
import Icon from "../../../components/Icon";
import { Icon as LucideIcon } from "lucide-react";
import { getIcon, getIconComponent } from "../../../helpers/lucideIcons";
import { getThemeLayout } from "../../../helpers/siteContent";

const rowAnchor = row => row.field ? `section-${row.field}` : (row.block ?? null);

const lines = text => (text ?? '').split('\n').filter(line => line.trim() !== '');

const Placeholders = ({ count, className }) => (
    <>
        {Array.from({ length: count }).map((_, index) => (
            <span key={index} className={className} />
        ))}
    </>
);

const RowContent = ({ row, content }) => {
    const values = row.block ? (content?.[row.block] ?? {}) : {};

    switch(row.type){
        case 'topbar':
            return (
                <div className="pv-topbar">
                    <span>{values.text}</span>
                </div>
            );

        case 'header':
            return (
                <div className="pv-header">
                    <span className="pv-logo">LOGO</span>
                    <span className="pv-search">
                        <Icon name="search" size={11} />
                        {values.placeholder || 'Buscar...'}
                    </span>
                    <span className="pv-header-icons">
                        <Placeholders count={3} className="pv-dot" />
                    </span>
                </div>
            );

        case 'menu':
            return (
                <div className="pv-menu">
                    <span className="pv-menu-all">{values.all_products}</span>
                    <Placeholders count={4} className="pv-menu-item" />
                    {(values.links ?? [])
                        .filter(link => (link.label ?? '').trim() !== '')
                        .map((link, index) => (
                            <span key={index} className="pv-menu-link">{link.label}</span>
                        ))}
                </div>
            );

        case 'banner':
            return (
                <div className="pv-banner">
                    <span>{row.caption}</span>
                </div>
            );

        case 'hero':
            return (
                <div className="pv-hero">
                    <small>{values.kicker}</small>
                    {lines(values.title).map((line, index) => (
                        <strong key={index}>{line}</strong>
                    ))}
                </div>
            );

        case 'categories':
            return (
                <div className="pv-categories">
                    {row.caption &&
                        <span className="pv-caption">{row.caption}</span>
                    }
                    <div className="pv-circles">
                        <Placeholders count={6} className="pv-circle" />
                    </div>
                </div>
            );

        case 'highlights':
            return (
                <div className="pv-highlights">
                    {(values.items ?? []).map((item, index) => {
                        const IconComponent = getIconComponent(item.icon);

                        return (
                            <div key={index} className="pv-highlight">
                                <span className="pv-highlight-icon">
                                    {!getIcon(item.icon) && Array.isArray(item.icon_node) ?
                                        <LucideIcon iconNode={item.icon_node} size={14} strokeWidth={2.2} />
                                    :
                                        <IconComponent size={14} strokeWidth={2.2} />
                                    }
                                </span>
                                <div>
                                    <b>{item.title}</b>
                                    {lines(item.subtitle).map((line, lineIndex) => (
                                        <em key={lineIndex}>{line}</em>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            );

        case 'curated':
            return (
                <div className="pv-block">
                    <h4>{values.title}</h4>
                    <p>{values.subtitle}</p>
                    <div className="pv-cards tall">
                        <Placeholders count={3} className="pv-card" />
                    </div>
                </div>
            );

        case 'section':
            return (
                <div className="pv-block">
                    <h4>{values[row.field]}</h4>
                    <div className="pv-cards">
                        <Placeholders count={4} className="pv-card" />
                    </div>
                </div>
            );

        case 'logos':
            return (
                <div className="pv-block">
                    <h4>{values[row.field]}</h4>
                    <div className="pv-logos">
                        <Placeholders count={5} className="pv-logo-item" />
                    </div>
                </div>
            );

        case 'testimonials':
            return (
                <div className="pv-block">
                    <h4>{values[row.field]}</h4>
                    <div className="pv-quotes">
                        <Placeholders count={2} className="pv-quote" />
                    </div>
                </div>
            );

        case 'text':
            return (
                <div className="pv-text">
                    <span className="pv-caption">{row.caption}</span>
                    <Placeholders count={3} className="pv-line" />
                </div>
            );

        default:
            return null;
    }
}

export const HomePreview = ({ slug, content, activeBlock, hoverAnchor, pick }) => {
    const layout = getThemeLayout(slug);

    return (
        <div className="home-preview">
            {layout.map((row, index) => {
                const anchor = rowAnchor(row);
                const editable = Boolean(row.block);
                const isActive = editable && row.block === activeBlock;
                const isHovered = hoverAnchor !== null && anchor === hoverAnchor;

                if(row.type === 'topbar' && (content?.[row.block]?.text ?? '') === '') return null;

                const className = [
                    'pv-row',
                    `pv-row-${row.type}`,
                    editable ? 'editable' : 'static',
                    isActive ? 'active' : '',
                    isHovered ? 'hovered' : ''
                ].filter(Boolean).join(' ');

                if(!editable){
                    return (
                        <div key={index} className={className}>
                            <RowContent row={row} content={content} />
                        </div>
                    );
                }

                return (
                    <button
                        key={index}
                        type="button"
                        className={className}
                        onClick={() => pick(row.block, row.field ?? null)}
                        title="Clique para editar este trecho"
                    >
                        <span className="pv-edit-badge">
                            <Icon name="edit" size={11} />
                            Editar
                        </span>
                        <RowContent row={row} content={content} />
                    </button>
                );
            })}
            <div className="pv-row pv-row-footer static">
                <div className="pv-footer">
                    <span className="pv-caption">Rodapé</span>
                    <Placeholders count={4} className="pv-footer-col" />
                </div>
            </div>
        </div>
    );
}

export const ProductPreview = ({ block, content, hoverAnchor, pick }) => {
    const values = content?.[block.key] ?? {};
    const descriptionSide = values.description_position === 'side';
    const specsSide = values.specs_position === 'side';
    const links = [
        !descriptionSide && 'Ver descrição completa',
        !specsSide && 'Ver especificações'
    ].filter(Boolean);
    const tabs = [
        !descriptionSide && 'Descrição',
        !specsSide && 'Especificações'
    ].filter(Boolean);

    const className = [
        'pv-row',
        'pv-row-product',
        'editable',
        'active',
        hoverAnchor === block.key ? 'hovered' : ''
    ].filter(Boolean).join(' ');

    return (
        <div className="home-preview">
            <div className="pv-row static">
                <RowContent row={{ type: 'header', block: 'search' }} content={content} />
            </div>
            <button type="button" className={className} onClick={() => pick(block.key)} title="Clique para editar este trecho">
                <span className="pv-edit-badge">
                    <Icon name="edit" size={11} />
                    Editar
                </span>
                <div className="pv-product">
                    <span className="pv-product-photo">FOTO</span>
                    <div className="pv-product-info">
                        <strong>Nome do produto</strong>
                        <div className={`pv-product-text ${descriptionSide ? 'full' : ''}`}>
                            <span className="pv-caption">{descriptionSide ? 'Descrição completa' : 'Resumo'}</span>
                            <Placeholders count={descriptionSide ? 7 : 3} className="pv-line" />
                        </div>
                        {specsSide &&
                            <div className="pv-product-specs">
                                <span className="pv-caption">Especificações</span>
                                <Placeholders count={3} className="pv-line" />
                            </div>
                        }
                        {links.length > 0 &&
                            <div className="pv-product-links">
                                {links.map(link => <span key={link}>{link}</span>)}
                            </div>
                        }
                        <span className="pv-product-cta">SOLICITAR ORÇAMENTO</span>
                    </div>
                </div>
            </button>
            {tabs.length > 0 &&
                <div className="pv-row static">
                    <div className="pv-block pv-text">
                        <h4>Sobre o produto</h4>
                        <div className="pv-product-tabs">
                            {tabs.map((tab, index) => (
                                <span key={tab} className={index === 0 ? 'active' : ''}>{tab}</span>
                            ))}
                        </div>
                        <Placeholders count={4} className="pv-line" />
                    </div>
                </div>
            }
            <div className="pv-row static">
                <div className="pv-block">
                    <h4>Produtos relacionados</h4>
                    <div className="pv-cards">
                        <Placeholders count={4} className="pv-card" />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default HomePreview;
